import {
  CalendarCheckIcon,
  ClockIcon,
  MapPinIcon,
  PhoneIcon,
  UserIcon,
  CarIcon,
} from "lucide-react";

import { useMemo } from "react";

import type { BookingConfirmationPayload } from "@/lib/api";
import { toBcp47 } from "@/i18n";
import { useLocale, useT } from "@/locale-context";

function capitalise(label: string): string {
  return label.length === 0 ? label : label.charAt(0).toUpperCase() + label.slice(1);
}

export function BookingConfirmationCard({
  data,
}: {
  data: BookingConfirmationPayload;
}) {
  const t = useT();
  const locale = useLocale();
  const bcp47 = toBcp47(locale);
  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(bcp47, { weekday: "long", day: "numeric", month: "long", year: "numeric" }),
    [bcp47],
  );
  const timeFmt = useMemo(
    () => new Intl.DateTimeFormat(bcp47, { hour: "2-digit", minute: "2-digit" }),
    [bcp47],
  );

  const start = new Date(data.startUtc);
  const end = new Date(data.endUtc);
  const acceptanceLabel =
    data.acceptanceType === "test-drive"
      ? t("booking.testDrive")
      : t("booking.showroomVisit");

  const isPending = data.status === "pending-confirmation";
  const borderClass = isPending ? "border-amber-300" : "border-emerald-300";
  const gradientClass = isPending
    ? "bg-gradient-to-br from-amber-500/10 to-orange-500/10"
    : "bg-gradient-to-br from-emerald-500/10 to-teal-500/10";
  const iconTint = isPending ? "text-amber-700" : "text-emerald-700";
  const heading = isPending
    ? t("booking.requested", { type: acceptanceLabel })
    : t("booking.confirmed", { type: acceptanceLabel });
  const subheading = isPending ? t("booking.pendingNote") : null;

  return (
    <div className={`my-3 rounded-xl border ${borderClass} ${gradientClass} p-4 shadow-sm`}>
      <div className="flex items-start gap-3">
        <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-white/80 shadow-sm">
          <CalendarCheckIcon className={`size-5 ${iconTint}`} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-neutral-900 text-sm leading-tight">
            {heading}
          </p>
          {subheading ? (
            <p className="mt-0.5 text-[11px] text-amber-800">{subheading}</p>
          ) : null}
          {!isPending && data.appointmentNumber ? (
            <p className="mt-0.5 text-neutral-500 text-[11px]">
              {t("booking.reference")}: {data.appointmentNumber}
            </p>
          ) : null}
        </div>
      </div>

      <dl className="mt-3 space-y-1.5 text-xs">
        <div className="flex items-start gap-2">
          <ClockIcon className="mt-0.5 size-3.5 shrink-0 text-neutral-500" />
          <div className="min-w-0">
            <dt className="sr-only">{t("booking.when")}</dt>
            <dd className="text-neutral-800">
              {capitalise(dateFmt.format(start))} {t("booking.timeConnector")} {timeFmt.format(start)} – {timeFmt.format(end)}
            </dd>
          </div>
        </div>

        <div className="flex items-start gap-2">
          <MapPinIcon className="mt-0.5 size-3.5 shrink-0 text-neutral-500" />
          <div className="min-w-0">
            <dt className="sr-only">{t("booking.where")}</dt>
            <dd className="text-neutral-800">
              <span className="font-medium">{data.dealer.name}</span>
              <br />
              <span className="text-neutral-600">{data.dealer.address}</span>
            </dd>
          </div>
        </div>

        {data.dealer.phone ? (
          <div className="flex items-start gap-2">
            <PhoneIcon className="mt-0.5 size-3.5 shrink-0 text-neutral-500" />
            <dd className="text-neutral-700">
              <a
                className="hover:underline"
                href={`tel:${data.dealer.phone.replace(/\s+/g, "")}`}
              >
                {data.dealer.phone}
              </a>
            </dd>
          </div>
        ) : null}

        {data.staffName ? (
          <div className="flex items-start gap-2">
            <UserIcon className="mt-0.5 size-3.5 shrink-0 text-neutral-500" />
            <dd className="text-neutral-700">{data.staffName}</dd>
          </div>
        ) : null}

        {data.vehicle ? (
          <div className="flex items-start gap-2">
            <CarIcon className="mt-0.5 size-3.5 shrink-0 text-neutral-500" />
            <dd className="text-neutral-700">{data.vehicle}</dd>
          </div>
        ) : null}
      </dl>
    </div>
  );
}
